import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Store, Users, TrendingUp, Calendar, Ban, CheckCircle } from 'lucide-react';
import { growthStats } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { useOutlets } from '../hooks/useOutlets';

gsap.registerPlugin(ScrollTrigger);

const GrowthSection = () => {
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];
  const { outlets } = useOutlets();
  const [counts, setCounts] = useState({
    outlets: 0,
    customers: 0,
    retention: 0,
    years: 0
  });
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top 70%',
        onEnter: () => {
          if (!hasAnimated) {
            setHasAnimated(true);
            animateCounters(outlets.length || growthStats.outlets);
          }
        }
      });

      gsap.fromTo('.growth-card',
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [hasAnimated, language, outlets]);

  useEffect(() => {
    if (hasAnimated && outlets.length > 0) {
      setCounts(prev => ({
        ...prev,
        outlets: outlets.length
      }));
    }
  }, [outlets, hasAnimated]);

  const getSublabel = () => {
    const uniqueZones = Array.from(new Set(outlets.map(o => o.zone).filter(Boolean)));
    const hasSatara = uniqueZones.some(z => z.toLowerCase().includes('satara'));
    const hasPune = uniqueZones.some(z => z.toLowerCase().includes('pune') || z.toUpperCase() === 'PCMC');
    
    if (hasPune && hasSatara) {
      return language === 'en' ? 'Pune, PCMC & Satara' : 'पुणे, पीसीएमसी आणि सातारा';
    }
    
    const nonPuneZones = uniqueZones.filter(z => !z.toLowerCase().includes('pune') && z.toUpperCase() !== 'PCMC');
    if (nonPuneZones.length > 1) {
      return language === 'en' ? 'Across Maharashtra' : 'महाराष्ट्रभर';
    }
    
    return language === 'en' ? 'Pune & PCMC' : 'पुणे आणि पीसीएमसी';
  };

  const animateCounters = (targetOutlets = growthStats.outlets) => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      setCounts({
        outlets: Math.round(targetOutlets * easeProgress),
        customers: Math.round(growthStats.customersServed * easeProgress),
        retention: Math.round(growthStats.franchiseRetention * easeProgress),
        years: Math.round(growthStats.yearsInBusiness * easeProgress)
      });

      if (step >= steps) {
        clearInterval(timer);
      }
    }, interval);
  };

  const stats = [
    {
      icon: Store,
      value: counts.outlets,
      suffix: '+',
      label: t.stats.outlets,
      sublabel: getSublabel(),
      color: 'red'
    },
    {
      icon: Users,
      value: counts.customers.toLocaleString(),
      suffix: '+',
      label: t.stats.customers,
      sublabel: language === 'en' ? 'Happy faces' : 'आनंदी चेहरे',
      color: 'yellow'
    },
    {
      icon: TrendingUp,
      value: counts.retention,
      suffix: '%',
      label: t.stats.retention,
      sublabel: language === 'en' ? 'Partner loyalty' : 'भागीदार निष्ठा',
      color: 'green'
    },
    {
      icon: Calendar,
      value: counts.years,
      suffix: '+',
      label: t.stats.years,
      sublabel: language === 'en' ? 'Growing strong' : 'वेगाने वाढत आहे',
      color: 'blue'
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 lg:py-32 bg-gradient-to-b from-white to-red-50 dark:from-gray-900 dark:to-black transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            100% {language === 'en' ? 'Organic Growth' : 'नैसर्गिक वाढ'}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {language === 'en' ? 'Our Numbers Speak' : 'आमची आकडेवारी'} <span className="text-red-600 dark:text-red-500">{language === 'en' ? 'Volumes' : 'बरंच काही सांगते'}</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {language === 'en' ? 'Growth driven by quality, not marketing budgets' : 'गुणवत्तेमुळे झालेली वाढ, मार्केटिंगमुळे नाही'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="growth-card bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 dark:border-gray-700"
            >
              <div className={`w-14 h-14 rounded-xl mb-4 flex items-center justify-center ${stat.color === 'red' ? 'bg-red-100 dark:bg-red-900/30' :
                stat.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                  stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                <stat.icon className={`w-7 h-7 ${stat.color === 'red' ? 'text-red-600 dark:text-red-400' :
                  stat.color === 'yellow' ? 'text-yellow-600 dark:text-yellow-400' :
                    stat.color === 'green' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'
                  }`} />
              </div>
              <div className="text-4xl font-bold text-gray-900 dark:text-white mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {stat.value}{stat.suffix}
              </div>
              <div className="text-lg font-semibold text-gray-800 dark:text-gray-200" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {stat.label}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {stat.sublabel}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-red-600 to-red-500 dark:from-red-700 dark:to-red-600 rounded-2xl p-8 md:p-10 shadow-xl">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Ban className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-xl" style={{ fontFamily: "'Poppins', sans-serif" }}>{language === 'en' ? 'No Paid Marketing' : 'विना जाहिरात'}</p>
                <p className="text-white/80" style={{ fontFamily: "'Poppins', sans-serif" }}>{language === 'en' ? 'Zero advertising spend' : 'इतर कोणत्याही जाहिरातीशिवाय'}</p>
              </div>
            </div>

            <div className="hidden md:block w-px h-16 bg-white/30"></div>

            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-xl" style={{ fontFamily: "'Poppins', sans-serif" }}>{language === 'en' ? 'Only Word of Mouth' : 'केवळ शब्दातून शब्द'}</p>
                <p className="text-white/80" style={{ fontFamily: "'Poppins', sans-serif" }}>{language === 'en' ? '& Repeat Customers' : 'आणि पुन्हा येणारे ग्राहक'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GrowthSection;
